import { useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Calendar,
  DollarSign,
  Package,
  TrendingUp,
  Users,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ShipmentProvider, useShipment } from "@/contexts/shipment-context";
import { customerService } from "@/lib/database";

function AnalyticsContent() {
  const { stats: shipmentStats, loadingStats, refreshStats } = useShipment();
  const [customerTotal, setCustomerTotal] = useState(0);
  const [loadingCustomers, setLoadingCustomers] = useState(true);

  useEffect(() => {
    refreshStats();
    loadCustomers();
  }, [refreshStats]);

  const loadCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const customerData = await customerService.getAll(1000);
      setCustomerTotal(customerData.customers.length);
    } catch (error) {
      console.error("Error loading analytics:", error);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const stats = useMemo(
    () => ({
      totalRevenue: shipmentStats.totalRevenue || 0,
      totalShipments: shipmentStats.totalShipments || 0,
      activeShipments: shipmentStats.activeShipments || 0,
      totalCustomers: customerTotal,
      monthlyRevenue: (shipmentStats.totalRevenue || 0) * 0.15,
      monthlyShipments: Math.floor((shipmentStats.totalShipments || 0) * 0.1),
    }),
    [customerTotal, shipmentStats]
  );

  const loading = loadingStats || loadingCustomers;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          Analytics & Reports
        </h1>
        <p className="text-gray-600">
          Detailed insights into your shipping business performance
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${stats.totalRevenue.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">All time revenue</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Shipments
            </CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalShipments.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">All time shipments</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Active Shipments
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeShipments}</div>
            <p className="text-xs text-muted-foreground">
              Currently in progress
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Customers
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalCustomers}</div>
            <p className="text-xs text-muted-foreground">
              Registered customers
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Monthly Revenue</CardTitle>
            <CardDescription>Revenue performance this month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#254164] mb-2">
              ${stats.monthlyRevenue.toLocaleString()}
            </div>
            <p className="text-sm text-gray-600">+12% from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Monthly Shipments</CardTitle>
            <CardDescription>Shipments processed this month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#254164] mb-2">
              {stats.monthlyShipments}
            </div>
            <p className="text-sm text-gray-600">+8% from last month</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Shipping Methods</CardTitle>
            <CardDescription>Distribution of shipping methods</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Air Shipping</span>
                <span className="text-sm text-gray-600">65%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-[#254164] h-2 rounded-full"
                  style={{ width: "65%" }}
                ></div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Sea Shipping</span>
                <span className="text-sm text-gray-600">35%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{ width: "35%" }}
                ></div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Shipment Timeline</CardTitle>
            <CardDescription>Recent shipment activity</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {["Week 1", "Week 2", "Week 3", "Week 4"].map((week, index) => (
                <div key={week} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-4 w-4 text-[#254164]" />
                    <span className="text-sm font-medium">{week}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <BarChart3 className="h-4 w-4 text-[#254164]" />
                    <span>
                      {Math.round(stats.totalShipments * 0.05 * (index + 1))}{" "}
                      shipments
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  return (
    <ShipmentProvider>
      <AnalyticsContent />
    </ShipmentProvider>
  );
}
